import { Controller } from "@hotwired/stimulus";

function formatTime(timeString) {
  const [hour, minute] = timeString.split(":");
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return "Tomorrow";
  } else {
    return date.toLocaleDateString([], { 
      weekday: "short", 
      month: "short", 
      day: "numeric" 
    });
  }
}

export default class extends Controller {
  static targets = [
    "providerField",
    "dateField", 
    "timeField",
    "dateScroller",
    "datePrompt",
    "timeSlots",
    "progressBar",
    "submitButton"
  ];

  static values = {
    baseUrl: String
  };

  connect() {
    this.updateProgress();
    this.setupFormValidation();

    const selectedProvider = this.element.querySelector(
      'input[name="appointment[service_provider_id]"]:checked'
    );
    if (selectedProvider) {
      this.providerFieldTarget.value = selectedProvider.value;
      this.fetchAvailableDates(selectedProvider.value);
      this.updateStepIndicator(1, true);
      this.updateProgress();
    }
  }

  setupFormValidation() {
    // Add event listeners for form fields to update progress
    const nameField = document.querySelector('input[name="appointment[customer_name]"]');
    const emailField = document.querySelector('input[name="appointment[customer_email]"]');
    const termsField = document.querySelector('input[name="appointment[terms_accepted]"]');

    [nameField, emailField, termsField].forEach(field => {
      if (field) {
        field.addEventListener('input', () => this.updateProgress());
        field.addEventListener('change', () => this.updateProgress());
      }
    });
  }

  async providerChanged(event) {
    const providerId = event.currentTarget.value;
    this.providerFieldTarget.value = providerId;

    // Update visual selection
    this.updateProviderSelection(event);
    
    // Clear previous selections
    this.dateFieldTarget.value = "";
    this.timeFieldTarget.value = "";
    this.timeSlotsTarget.innerHTML = "Please select a date first";

    // Fetch available dates
    await this.fetchAvailableDates(providerId);
    
    this.updateProgress();
  }

  updateProviderSelection(event) {
    // Remove selection from all provider cards
    const allProviderCards = document.querySelectorAll('input[name="appointment[service_provider_id]"]');
    allProviderCards.forEach(radio => {
      const card = radio.closest('label').querySelector('div');
      card.classList.remove(
        'border-indigo-500', 'bg-indigo-50/50', 'dark:bg-indigo-900/20', 'shadow-lg'
      );
      card.classList.add('border-slate-200', 'dark:border-slate-700');
    });

    // Add selection to current card
    const selectedCard = event.currentTarget.closest('label').querySelector('div');
    selectedCard.classList.remove('border-slate-200', 'dark:border-slate-700');
    selectedCard.classList.add(
      'border-indigo-500', 'bg-indigo-50/50', 'dark:bg-indigo-900/20', 'shadow-lg'
    );

    // Update step indicator
    this.updateStepIndicator(1, true);
  }

  async fetchAvailableDates(providerId) {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/providers/${providerId}/available_dates`
      );
      
      if (!response.ok) throw new Error("Failed to fetch dates");
      
      const dates = await response.json();
      this.renderDateOptions(dates);
      
    } catch (error) {
      console.error("Error fetching available dates:", error);
      this.dateScrollerTarget.classList.add("hidden");
      this.datePromptTarget.textContent = "Error loading available dates. Please try again.";
    }
  }

  renderDateOptions(dates) {
    if (!dates || dates.length === 0) {
      this.dateScrollerTarget.classList.add("hidden");
      this.datePromptTarget.textContent = "No available dates found for this provider.";
      return;
    }

    this.datePromptTarget.classList.add("hidden");
    this.dateScrollerTarget.classList.remove("hidden");

    const dateContainer = this.dateScrollerTarget.querySelector('.flex');
    
    const html = dates.slice(0, 14).map(date => `
      <button type="button"
              class="flex-shrink-0 px-6 py-4 rounded-lg border-2 border-slate-200 dark:border-slate-700
                     bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm
                     text-center hover:border-indigo-300 transition-all duration-200
                     focus:ring-2 focus:ring-indigo-500"
              data-action="click->quick-booking-form#selectDate"
              data-date="${date}">
        <div class="text-sm font-medium text-slate-900 dark:text-white">
          ${formatDate(date)}
        </div>
        <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          ${new Date(date).toLocaleDateString([], { month: "short", day: "numeric" })}
        </div>
      </button>
    `).join("");

    dateContainer.innerHTML = html;
  }

  async selectDate(event) {
    const dateStr = event.currentTarget.dataset.date;
    if (!dateStr) return;

    this.dateFieldTarget.value = dateStr;

    // Update visual selection
    this.updateDateSelection(event.currentTarget);

    // Clear time selection
    this.timeFieldTarget.value = "";

    // Fetch time slots
    const providerId = this.providerFieldTarget.value;
    if (providerId) {
      await this.fetchTimeSlots(providerId, dateStr);
    }

    this.updateProgress();
  }

  updateDateSelection(selectedButton) {
    // Remove selection from all date buttons
    const allDateButtons = this.dateScrollerTarget.querySelectorAll('button');
    allDateButtons.forEach(btn => {
      btn.classList.remove(
        'border-indigo-500', 'bg-indigo-50/50', 'dark:bg-indigo-900/20', 'shadow-lg'
      );
      btn.classList.add('border-slate-200', 'dark:border-slate-700');
    });

    // Add selection to current button
    selectedButton.classList.remove('border-slate-200', 'dark:border-slate-700');
    selectedButton.classList.add(
      'border-indigo-500', 'bg-indigo-50/50', 'dark:bg-indigo-900/20', 'shadow-lg'
    );

    // Update step indicator
    this.updateStepIndicator(2, true);
  }

  async fetchTimeSlots(providerId, date) {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/providers/${providerId}/slots?date=${date}`
      );
      
      if (!response.ok) throw new Error("Failed to fetch time slots");
      
      const slots = await response.json();
      this.renderTimeSlots(slots);
      
    } catch (error) {
      console.error("Error fetching time slots:", error);
      this.timeSlotsTarget.innerHTML = `
        <div class="text-center py-8 text-red-600 dark:text-red-400">
          Error loading time slots. Please try again.
        </div>
      `;
    }
  }

  renderTimeSlots(slots) {
    if (!slots || slots.length === 0) {
      this.timeSlotsTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="text-slate-600 dark:text-slate-400 mb-2">
            No available time slots for this date
          </div>
          <div class="text-sm text-slate-500 dark:text-slate-500">
            Please try a different date
          </div>
        </div>
      `;
      return;
    }

    const availableSlots = slots.filter(slot => slot.available);
    
    if (availableSlots.length === 0) {
      this.timeSlotsTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="text-slate-600 dark:text-slate-400 mb-2">
            All time slots are booked for this date
          </div>
          <div class="text-sm text-slate-500 dark:text-slate-500">
            Please try a different date
          </div>
        </div>
      `;
      return;
    }

    const html = `
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        ${availableSlots.map(slot => `
          <button type="button"
                  class="px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700
                         bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm
                         text-sm font-medium text-slate-700 dark:text-slate-300
                         hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20
                         transition-all duration-200 focus:ring-2 focus:ring-indigo-500"
                  data-action="click->quick-booking-form#selectTime"
                  data-time="${slot.time}">
            ${formatTime(slot.time)}
          </button>
        `).join("")}
      </div>
    `;

    this.timeSlotsTarget.innerHTML = html;
  }

  selectTime(event) {
    const time = event.currentTarget.dataset.time;
    if (!time) return;

    this.timeFieldTarget.value = time;

    // Update visual selection
    this.updateTimeSelection(event.currentTarget);
    this.updateProgress();
  }

  updateTimeSelection(selectedButton) {
    // Remove selection from all time buttons
    const allTimeButtons = this.timeSlotsTarget.querySelectorAll('button');
    allTimeButtons.forEach(btn => {
      btn.classList.remove(
        'border-indigo-500', 'bg-indigo-500', 'text-white'
      );
      btn.classList.add(
        'border-slate-200', 'dark:border-slate-700', 
        'text-slate-700', 'dark:text-slate-300'
      );
    });

    // Add selection to current button
    selectedButton.classList.remove(
      'border-slate-200', 'dark:border-slate-700', 
      'text-slate-700', 'dark:text-slate-300'
    );
    selectedButton.classList.add('border-indigo-500', 'bg-indigo-500', 'text-white');

    // Update step indicator
    this.updateStepIndicator(3, true);
  }

  updateStepIndicator(step, completed) {
    const stepIndicators = document.querySelectorAll('.w-8.h-8.rounded-full');
    
    if (stepIndicators[step - 1]) {
      const indicator = stepIndicators[step - 1];
      
      if (completed) {
        indicator.classList.remove('bg-slate-100', 'dark:bg-slate-700');
        indicator.classList.add('bg-indigo-100', 'dark:bg-indigo-900/50');
        
        const stepNumber = indicator.querySelector('span');
        stepNumber.classList.remove('text-slate-500', 'dark:text-slate-400');
        stepNumber.classList.add('text-indigo-600', 'dark:text-indigo-400');
      }
    }
  }

  updateProgress() {
    const providerId = this.providerFieldTarget.value;
    const date = this.dateFieldTarget.value;
    const time = this.timeFieldTarget.value;

    let progress = 25; // Base progress

    if (providerId) progress = 40;
    if (date) progress = 65;
    if (time) progress = 85;

    // Check if form is ready to submit
    const name = document.querySelector('input[name="appointment[customer_name]"]')?.value;
    const email = document.querySelector('input[name="appointment[customer_email]"]')?.value;
    const terms = document.querySelector('input[name="appointment[terms_accepted]"]')?.checked;

    if (providerId && date && time && name && email && terms) {
      progress = 100;
      this.submitButtonTarget.disabled = false;
      this.updateStepIndicator(4, true);
    } else {
      this.submitButtonTarget.disabled = true;
    }

    this.progressBarTarget.style.width = `${progress}%`;
  }
}
